import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useContext } from "react";
import { AppContext } from "../context/app";
import {
  CreatePlanCategoryBody,
  CreatePlanCategoryResult,
  CreatePlanPayload,
  CreatePlanResult,
  deletePlanCategory,
  DeletePlanCategoryBody,
  getPlan,
  getPlanCategories,
  GetPlanCategoriesResult,
  getPlanCategory,
  GetPlanResult,
  getPlans,
  getSubscriptionMigratePreview,
  GetSubscriptionMigratePreviewParams,
  getUserProfileSubscriptions,
  patchPlanCategory,
  Plan,
  PlanCategory,
  postPlan,
  postPlanCategory,
  putPlan,
  SubscriptionMigratePreview,
  UpdatePlanCategoryBody,
  UpdatePlanCategoryResult,
  UpdatePlanPayload,
  UpdatePlanResult,
} from "./subscriptionApi";

export function usePlansQuery(
  include?: "activeSubscriptionsCount"[],
  options?: UseQueryOptions<Plan[]>,
) {
  return useQuery({
    ...options,
    queryKey: ["plans", include?.join()],
    queryFn: () => getPlans(include),
  });
}

export function usePlanCategoriesQuery(
  options?: UseQueryOptions<GetPlanCategoriesResult>,
) {
  const app = useContext(AppContext);
  return useQuery({
    ...options,
    queryKey: ["plan-categories"],
    queryFn: () => getPlanCategories(app.client.id as number),
  });
}

export function usePostPlanMutation(
  options?: UseMutationOptions<CreatePlanResult, unknown, CreatePlanPayload>,
) {
  return useMutation({
    ...options,
    mutationFn: (payload: CreatePlanPayload) => {
      return postPlan(payload);
    },
  });
}

export function usePlanQuery(
  planUuid: string,
  planOptions: { include?: "activeSubscriptionsCount"[] } = {},
  options?: UseQueryOptions<GetPlanResult>,
) {
  return useQuery({
    ...options,
    queryKey: ["plan", planUuid],
    queryFn: () => getPlan(planUuid, planOptions),
  });
}

export function usePutPlanMutation(
  options?: UseMutationOptions<UpdatePlanResult, unknown, UpdatePlanPayload>,
) {
  return useMutation({
    ...options,
    mutationFn: (payload: UpdatePlanPayload) => {
      return putPlan(payload);
    },
  });
}

export function useSubscriptionMigratePreviewQuery(
  params: GetSubscriptionMigratePreviewParams,
  options?: UseQueryOptions<SubscriptionMigratePreview>,
) {
  const {
    subscriptionUuid,
    newPlanUuid,
    discountUuid,
    billingUuid,
    when,
    customSetupFee,
  } = params;
  return useQuery({
    ...options,
    queryKey: [
      "subscription-migrate-preview",
      subscriptionUuid,
      newPlanUuid,
      discountUuid,
      billingUuid,
      when,
      customSetupFee,
    ],
    queryFn: () => getSubscriptionMigratePreview(params),
  });
}

export function usePostPlanCategoryMutation(
  options?: UseMutationOptions<
    CreatePlanCategoryResult,
    unknown,
    CreatePlanCategoryBody
  >,
) {
  return useMutation({
    ...options,
    mutationFn: (body: CreatePlanCategoryBody) => {
      return postPlanCategory(body);
    },
  });
}

export function usePatchPlanCategoryMutation(
  options?: UseMutationOptions<
    UpdatePlanCategoryResult,
    unknown,
    UpdatePlanCategoryBody
  >,
) {
  return useMutation({
    ...options,
    mutationFn: (body: UpdatePlanCategoryBody) => {
      return patchPlanCategory(body);
    },
  });
}

export function usePlanCategoryQuery(
  planCategoryUuid: string,
  options?: UseQueryOptions<PlanCategory>,
) {
  return useQuery({
    ...options,
    queryKey: ["plan-category", planCategoryUuid],
    queryFn: () => getPlanCategory(planCategoryUuid),
  });
}

export function useDeletePlanCategoryMutation(
  options?: UseMutationOptions<void, unknown, DeletePlanCategoryBody>,
) {
  return useMutation({
    ...options,
    mutationFn: (body: DeletePlanCategoryBody) => {
      return deletePlanCategory(body);
    },
  });
}

export function useUserSubscriptionsQuery(userUuid: string) {
  const app = useContext(AppContext);

  return useQuery({
    queryKey: ["user-profile-subscriptions"],
    queryFn: () => getUserProfileSubscriptions(userUuid, app.client.uuid),
  });
}
